import { ApiError } from "api/client";
import { addEntityTreeLink, createEntity, getTreeEntities, removeEntityTreeLink, updateEntity } from "api/entities";
import { addRelation, getEntityRelations, removeRelation } from "api/relations";
import type { CustomPropertyDto, EntityDto, EntityRelationDto, LinkedTreeDto, ReferenceLinkDto } from "api/types";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { flipRelationPerspective } from "utilities";

import CustomAttrRow from "./CustomAttrRow";
import { byEntityTitle } from "./entityFormConfigs";
import type {
	CustomProp,
	EntityDialogProps,
	EntityFieldConfig,
	EntityFormConfig,
	ReferenceLinkDraft,
	RelationDraft,
	RelationOption,
	TreeLinkDraft
} from "./entityForms.types";
import EntityImageGallery from "./EntityImageGallery";
import FormField from "./FormField";
import LineIcon from "./LineIcon";
import ReferenceLinkRow from "./ReferenceLinkRow";
import RelationChipItem from "./RelationChipItem";
import RelationTypePicker from "./RelationTypePicker";
import TreeLinkPicker from "./TreeLinkPicker";
import { useEntityImages } from "./useEntityImages";

import styles from "./formDialog.module.css";

type EntityFormDialogProps = EntityDialogProps & {
	config: EntityFormConfig;
};

const newId = (): string =>
	typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/** Reads a string field off the loosely-typed EntityDto by payload key. */
const readEntityField = (entity: EntityDto | null | undefined, key: string): string => {
	const value = (entity as Record<string, unknown> | null | undefined)?.[key];

	return typeof value === "string" ? value : "";
};

const createInitialForm = (config: EntityFormConfig, entity?: EntityDto | null): Record<string, string> => {
	const form: Record<string, string> = {
		description: entity?.description ?? "",
		name: entity?.title ?? ""
	};
	config.fields.forEach((field: EntityFieldConfig) => {
		form[field.key] = readEntityField(entity, field.key);
	});

	return form;
};

const EntityFormDialog = ({ config, entity, treeId, onClose, onCreated }: EntityFormDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const isEdit = Boolean(entity);
	const idPrefix = config.entityType.toLowerCase();
	const tr = useCallback((suffix: string): string => t(`${config.i18nPrefix}.${suffix}`), [config.i18nPrefix, t]);

	const [form, setForm] = useState<Record<string, string>>(() => createInitialForm(config, entity));
	const gallery = useEntityImages({
		entityId: entity?.id ?? null,
		initialImages: entity?.images,
		getAccessToken
	});
	const [customProps, setCustomProps] = useState<CustomProp[]>(
		() =>
			entity?.customProperties?.map((prop: CustomPropertyDto) => ({
				id: prop.id,
				key: prop.key,
				value: prop.value ?? ""
			})) ?? []
	);
	const [referenceLinks, setReferenceLinks] = useState<ReferenceLinkDraft[]>(
		() => entity?.referenceLinks?.map((link: ReferenceLinkDto) => ({ id: link.id, url: link.url })) ?? []
	);
	const [treeLinks, setTreeLinks] = useState<TreeLinkDraft[]>(
		() =>
			entity?.linkedTrees?.map((link: LinkedTreeDto) => ({
				id: link.id,
				existingId: link.id,
				treeId: link.treeId,
				title: link.title
			})) ?? []
	);
	const [removedTreeLinkIds, setRemovedTreeLinkIds] = useState<string[]>([]);
	const [treeEntities, setTreeEntities] = useState<EntityDto[]>([]);
	const [associationType, setAssociationType] = useState<string>("");
	const [associationTarget, setAssociationTarget] = useState<string>("");
	const [associationIsEx, setAssociationIsEx] = useState(false);
	const [associations, setAssociations] = useState<RelationDraft[]>([]);
	const [removedAssociationIds, setRemovedAssociationIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");

	// Null until a type is picked, so an untouched dropdown can no longer record a relation
	// the author never chose. Everything downstream must cope with "nothing selected yet".
	const selectedOption = useMemo<RelationOption | null>(
		() => config.associationOptions.find((option: RelationOption) => option.value === associationType) ?? null,
		[associationType, config.associationOptions]
	);
	const availableTargets = useMemo<EntityDto[]>(
		() =>
			selectedOption
				? treeEntities
						.filter((item: EntityDto) => selectedOption.targetTypes.includes(item.type))
						.sort(byEntityTitle)
				: [],
		[selectedOption, treeEntities]
	);

	useEffect(() => {
		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getTreeEntities(treeId, token) : null))
			.then((list: EntityDto[] | null) => {
				if (active && list) {
					setTreeEntities(list.filter((item: EntityDto) => item.id !== entity?.id));
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity?.id, getAccessToken, treeId]);

	// Edit mode: show the entity's existing associations so they can be removed.
	useEffect(() => {
		if (!entity) {
			return undefined;
		}

		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getEntityRelations(entity.id, token) : null))
			.then((list: EntityRelationDto[] | null) => {
				if (active && list) {
					setAssociations(
						list.map((relation: EntityRelationDto) => ({
							id: relation.id,
							existingId: relation.id,
							relationType: flipRelationPerspective(relation.relationType),
							isEx: relation.isEx,
							targetId: relation.relatedEntityId,
							targetTitle: relation.relatedEntityTitle
						}))
					);
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity, getAccessToken]);

	const updateField = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const { name, value } = event.target;
		setForm((current: Record<string, string>) => ({ ...current, [name]: value }));
	}, []);

	const addCustomProp = useCallback((): void => {
		setCustomProps((current: CustomProp[]) => [...current, { id: newId(), key: "", value: "" }]);
	}, []);

	const updateCustomProp = useCallback((id: string, field: "key" | "value", value: string): void => {
		setCustomProps((current: CustomProp[]) =>
			current.map((prop: CustomProp) => (prop.id === id ? { ...prop, [field]: value } : prop))
		);
	}, []);

	const removeCustomProp = useCallback((id: string): void => {
		setCustomProps((current: CustomProp[]) => current.filter((prop: CustomProp) => prop.id !== id));
	}, []);

	const addReferenceLink = useCallback((): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) => [...current, { id: newId(), url: "" }]);
	}, []);

	const updateReferenceLink = useCallback((id: string, url: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.map((link: ReferenceLinkDraft) => (link.id === id ? { ...link, url } : link))
		);
	}, []);

	const removeReferenceLink = useCallback((id: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.filter((link: ReferenceLinkDraft) => link.id !== id)
		);
	}, []);

	const addTreeLink = useCallback((linkTreeId: string, title: string): void => {
		setTreeLinks((current: TreeLinkDraft[]) => [...current, { id: newId(), treeId: linkTreeId, title }]);
	}, []);

	const removeTreeLink = useCallback(
		(id: string): void => {
			const removed = treeLinks.find((link: TreeLinkDraft) => link.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedTreeLinkIds((ids: string[]) => [...ids, existingId]);
			}

			setTreeLinks((current: TreeLinkDraft[]) => current.filter((link: TreeLinkDraft) => link.id !== id));
		},
		[treeLinks]
	);

	const addAssociationDraft = useCallback((): void => {
		if (!selectedOption || !associationTarget) {
			return;
		}

		const target = availableTargets.find((item: EntityDto) => item.id === associationTarget);
		if (!target) {
			return;
		}

		setAssociations((current: RelationDraft[]) => [
			...current,
			{
				id: newId(),
				relationType: associationType,
				isEx: Boolean(selectedOption.canBeEx) && associationIsEx,
				targetId: target.id,
				targetTitle: target.title
			}
		]);
		setAssociationTarget("");
		setAssociationIsEx(false);
	}, [associationIsEx, associationTarget, associationType, availableTargets, selectedOption]);

	const removeAssociationDraft = useCallback(
		(id: string): void => {
			const removed = associations.find((association: RelationDraft) => association.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedAssociationIds((ids: string[]) => [...ids, existingId]);
			}

			setAssociations((current: RelationDraft[]) =>
				current.filter((association: RelationDraft) => association.id !== id)
			);
		},
		[associations]
	);

	const onAssociationTypeChange = useCallback((nextType: string): void => {
		setAssociationType(nextType);
		// The new type may allow a different set of targets, and may not support the ex modifier.
		setAssociationTarget("");
		setAssociationIsEx(false);
	}, []);

	const onAssociationTargetChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setAssociationTarget(event.target.value);
	}, []);

	const onAssociationIsExChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setAssociationIsEx(event.target.checked);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const name = form.name.trim();
			if (!name) {
				setError(tr("nameRequired"));

				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setError("");
			setIsSaving(true);

			try {
				const typedFields: Record<string, string | undefined> = {};
				config.fields.forEach((field: EntityFieldConfig) => {
					typedFields[field.key] = form[field.key].trim() || undefined;
				});

				const fields = {
					title: name,
					...typedFields,
					description: form.description.trim() || undefined,
					customProperties: customProps
						.filter((prop: CustomProp) => prop.key.trim())
						.map((prop: CustomProp) => ({
							key: prop.key.trim(),
							value: prop.value.trim() || undefined
						})),
					referenceLinks: referenceLinks.map((link: ReferenceLinkDraft) => link.url.trim()).filter(Boolean)
				};

				const saved = entity
					? await updateEntity(entity.id, { ...fields, images: gallery.toInputPayload() }, token)
					: await createEntity(
							treeId,
							{ type: config.entityType, ...fields, images: gallery.toInputPayload() },
							token
					  );

				await Promise.all(removedAssociationIds.map((relationId: string) => removeRelation(relationId, token)));

				// Every association reads "this entity is <type> of the target", so this entity is the From side.
				await Promise.all(
					associations
						.filter((association: RelationDraft) => !association.existingId)
						.map((association: RelationDraft) =>
							addRelation(
								{
									fromEntityId: saved.id,
									toEntityId: association.targetId,
									relationType: association.relationType,
									isEx: association.isEx
								},
								token
							)
						)
				);

				await Promise.all(removedTreeLinkIds.map((linkId: string) => removeEntityTreeLink(linkId, token)));
				await Promise.all(
					treeLinks
						.filter((link: TreeLinkDraft) => !link.existingId)
						.map((link: TreeLinkDraft) => addEntityTreeLink(saved.id, link.treeId, token))
				);

				onCreated(saved.id);
				onClose();
			} catch (submitError) {
				setError(submitError instanceof ApiError ? submitError.message : tr("saveError"));
			} finally {
				setIsSaving(false);
			}
		},
		[
			associations,
			config,
			customProps,
			entity,
			form,
			gallery,
			getAccessToken,
			onClose,
			onCreated,
			referenceLinks,
			removedAssociationIds,
			removedTreeLinkIds,
			tr,
			treeId,
			treeLinks
		]
	);

	const hasTargets = availableTargets.length > 0;

	return (
		<div className={styles.personPanelOverlay}>
			<form
				ref={dialogRef}
				aria-labelledby={`${idPrefix}-form-title`}
				aria-modal="true"
				className={styles.personFormPanel}
				onSubmit={onSubmit}
				role="dialog">
				<div className={styles.personFormHeader}>
					<div className={styles.personFormHeaderCopy}>
						<h1 id={`${idPrefix}-form-title`}>{isEdit ? tr("editTitle") : tr("addTitle")}</h1>
						<p>{tr("subtitle")}</p>
					</div>

					<button
						aria-label={tr("close")}
						className={styles.personFormCloseButton}
						onClick={onClose}
						type="button">
						<LineIcon name="close" />
					</button>
				</div>

				<div className={styles.personFormBody}>
					<div className={styles.personFormProfile}>
						<EntityImageGallery gallery={gallery} title={form.name} editable />
					</div>

					<div className={styles.personFormFields}>
						<h2>{tr("standardAttributes")}</h2>

						<div className={styles.personFormGrid}>
							<div className={styles.personFormField}>
								<label htmlFor={`${idPrefix}-name`}>
									{tr("name")} <span className={styles.requiredStar}>*</span>
								</label>
								<input
									id={`${idPrefix}-name`}
									maxLength={fieldLimits.entityTitle}
									name="name"
									onChange={updateField}
									type="text"
									value={form.name}
									required
								/>
							</div>

							{config.fields.map((field: EntityFieldConfig) => (
								<FormField
									key={field.key}
									field={field}
									idPrefix={idPrefix}
									label={tr(field.labelKey)}
									onChange={updateField}
									value={form[field.key]}
								/>
							))}

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor={`${idPrefix}-description`}>{tr("description")}</label>
								<textarea
									id={`${idPrefix}-description`}
									maxLength={fieldLimits.customPropertyValue}
									name="description"
									onChange={updateField}
									rows={4}
									value={form.description}
								/>
							</div>
						</div>
					</div>

					<section className={styles.personFormSection}>
						<h2>{tr("associations")}</h2>
						<div className={styles.relationAddRow}>
							<RelationTypePicker
								controlClassName={styles.relationSelect}
								disabled={isSaving}
								onChange={onAssociationTypeChange}
								options={config.associationOptions}
								value={associationType}
							/>
							<select
								aria-label={tr("associationTarget")}
								className={styles.relationSelect}
								disabled={!hasTargets || isSaving}
								onChange={onAssociationTargetChange}
								value={associationTarget}>
								<option value="">{tr("associationTargetPlaceholder")}</option>
								{availableTargets.map((target: EntityDto) => (
									<option key={target.id} value={target.id}>
										{`${target.title} (${target.type})`}
									</option>
								))}
							</select>

							{selectedOption?.canBeEx && (
								<label className={styles.relationExToggle} htmlFor={`${idPrefix}-association-is-ex`}>
									<input
										checked={associationIsEx}
										disabled={isSaving}
										id={`${idPrefix}-association-is-ex`}
										onChange={onAssociationIsExChange}
										type="checkbox"
									/>
									<span>{tr("formerAssociation")}</span>
								</label>
							)}
							<button
								className={styles.relationAddButton}
								disabled={!associationType || !associationTarget || isSaving}
								onClick={addAssociationDraft}
								type="button">
								<LineIcon name="plus" />
								<span>{tr("addAssociation")}</span>
							</button>
						</div>

						{!selectedOption && (
							<p className={styles.relationHint}>{t("editor.relationPicker.chooseTypeHint")}</p>
						)}
						{selectedOption && !hasTargets && <p className={styles.relationHint}>{tr("noTargetsHint")}</p>}

						{associations.length > 0 && (
							<div className={styles.relationChips}>
								{associations.map((association: RelationDraft) => (
									<RelationChipItem
										key={association.id}
										onRemove={removeAssociationDraft}
										relation={association}
										removeLabel={tr("removeAssociation")}
									/>
								))}
							</div>
						)}
					</section>

					<TreeLinkPicker
						currentTreeId={treeId}
						links={treeLinks}
						onAdd={addTreeLink}
						onRemove={removeTreeLink}
					/>

					<section className={styles.personFormSection}>
						<h2>{tr("customAttributes")}</h2>
						{customProps.map((prop: CustomProp) => (
							<CustomAttrRow
								key={prop.id}
								idPrefix={idPrefix}
								keyLabel={tr("key")}
								onChange={updateCustomProp}
								onRemove={removeCustomProp}
								prop={prop}
								removeLabel={tr("removeCustomAttribute")}
								valueLabel={tr("value")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addCustomProp} type="button">
							<LineIcon name="plus" />
							<span>{tr("addCustomAttribute")}</span>
						</button>
					</section>

					<section className={styles.personFormSection}>
						<h2>{tr("references")}</h2>
						{referenceLinks.map((link: ReferenceLinkDraft) => (
							<ReferenceLinkRow
								key={link.id}
								link={link}
								onChange={updateReferenceLink}
								onRemove={removeReferenceLink}
								removeLabel={tr("removeReference")}
								urlLabel={tr("referenceUrl")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addReferenceLink} type="button">
							<LineIcon name="plus" />
							<span>{tr("addReference")}</span>
						</button>
					</section>

					{error && <div className={styles.personFormError}>{error}</div>}
				</div>

				<div className={styles.personFormActions}>
					<button
						className={styles.personFormCancelButton}
						disabled={isSaving}
						onClick={onClose}
						type="button">
						{tr("cancel")}
					</button>
					<button className={styles.personFormSaveButton} disabled={isSaving} type="submit">
						{isSaving ? tr("saving") : tr("save")}
					</button>
				</div>
			</form>
		</div>
	);
};

export default EntityFormDialog;
